import assetUtils, { Asset } from 'app-lib/assetUtils'
import providerUtils from 'app-lib/providerUtils'
import BigNumber from 'bignumber.js'
import { chainConfigs, ZERO_ADDRESS } from '../config/appConfig'
import { AnyProvider } from './providerUtils'

/**
 * getSwapExpectedReturn
 * * params.isAmountIn = true: srcAmount is the amount we want to swap
 * * params.isAmountIn = false: srcAmount is the amount we want to obtain after swap
 */
export async function getSwapExpectedReturn(
  provider: AnyProvider,
  params: {
    srcToken: Asset
    destToken: Asset
    srcAmount: BigNumber
    loanDataBytes: string
    isAmountIn?: boolean
  }
) {
  const { contractsSource, chainId } = providerUtils.getDependenciesStrictRead(provider)
  const traderAddress = provider.currentAccount || ZERO_ADDRESS
  const srcAddress = assetUtils.getAddress(chainId, params.srcToken)
  const destAddress = assetUtils.getAddress(chainId, params.destToken)
  const destDecimals = assetUtils.getDecimals(chainId, params.destToken)
  const bzxContract = contractsSource.getiBZxContract()
  const tx = bzxContract.getSwapExpectedReturn(
    traderAddress,
    srcAddress,
    destAddress,
    params.srcAmount,
    params.loanDataBytes,
    params.isAmountIn || true
  )

  const destTokenAmount = await tx.callAsync()
  return destTokenAmount.div(10 ** destDecimals)
}

/**
 * Get swap rate for trade of 1 Native Token (ETH/BNB/MATIC etc.) worth volume
 */
export const getOneEthWorthTokenAmount = async (
  provider: AnyProvider,
  destToken: Asset,
  opts: { loanDataBytes: string }
) => {
  const { contractsSource, chainId } = providerUtils.getDependenciesStrictRead(provider)
  const traderAddress = provider.currentAccount || ZERO_ADDRESS
  const srcToken = chainConfigs[chainId].native.wrapped
  const srcAddress = assetUtils.getAddress(chainId, srcToken)
  const destAddress = assetUtils.getAddress(chainId, destToken)
  const srcDecimals = assetUtils.getDecimals(chainId, srcToken)
  const srcAmount = new BigNumber(10 ** srcDecimals)

  if (srcToken === destToken) {
    return srcAmount
  }
  const bzxContract = contractsSource.getiBZxContract()
  const tx = bzxContract.getSwapExpectedReturn(
    traderAddress,
    srcAddress,
    destAddress,
    srcAmount,
    opts.loanDataBytes,
    false
  )

  const destTokenAmount = await tx.callAsync()
  return destTokenAmount
}

/**
 * This function should be used to get real swap rate price.
 * For reference swap rate, use oracle api
 */
export async function getActualSwapRate(
  provider: AnyProvider,
  params: {
    srcToken: Asset
    destToken: Asset
    leverage?: number
    srcAmount?: BigNumber
    loanDataBytes: string
    isAmountIn?: boolean
  }
) {
  const { contractsSource, chainId } = providerUtils.getDependenciesStrictRead(provider)
  const { srcToken, destToken } = params
  const traderAddress = provider.currentAccount || ZERO_ADDRESS
  const { address: srcAddress, decimals: srcDecimals } = assetUtils.getInfo(chainId, srcToken)
  const { address: destAddress, decimals: destDecimals } = assetUtils.getInfo(chainId, destToken)

  const srcAmount =
    params.srcAmount || (await getOneEthWorthTokenAmount(provider, srcToken, params))

  if (srcToken === destToken) {
    // Note: This wasnt returning a rate before,
    // but the srcAmount which isnt what the function was supposed to return
    // now making it throw an error to find if that case actually happens
    throw new Error('srcToken is same as destToken')
  }

  const srcSwapAmount = srcAmount.times(params.leverage || 1)
  const bzxContract = contractsSource.getiBZxContract()

  const tx = bzxContract.getSwapExpectedReturn(
    traderAddress,
    srcAddress,
    destAddress,
    srcSwapAmount,
    params.loanDataBytes,
    params.isAmountIn || true
  )

  const destTokenAmount = await tx.callAsync()
  const receivedAmount = destTokenAmount.div(10 ** destDecimals)
  const sentAmount = srcSwapAmount.div(10 ** srcDecimals)
  const rate = receivedAmount.div(sentAmount).dp(18)
  return { srcToken, receivedAmount, destToken, sentAmount, rate }
}

/**
 * Get slippage rates by following Kyber's guide https://developer.kyber.network/docs/Integrations-SlippageRateProtection/
 * TODO: Kyber docs dont exist nor is it clear if it's the right way to do it. NEED to confirm it's good or rewrite it.
 * @returns
 */
export const getTradeSlippageRate = async (
  provider: AnyProvider,
  params: {
    srcToken: Asset
    destToken: Asset
    amount: BigNumber
    loanDataBytes: string
    /** swapRateForAmount rate or price of the srcToken for destToken for a certain amount  */
    swapPriceTradeAmount?: BigNumber
    isAmountIn: boolean
  }
): Promise<BigNumber> => {
  const { chainId } = providerUtils.getDependenciesStrictRead(provider)

  const { amount, srcToken, destToken } = params
  if (amount.eq(0) || srcToken === destToken) {
    return new BigNumber(0)
  }

  const srcDecimals = assetUtils.getDecimals(chainId, srcToken)
  const srcAmount = amount.dp(0, BigNumber.ROUND_HALF_UP)
  if (amount.lte(10 ** srcDecimals)) {
    return new BigNumber(0)
  }
  try {
    const { rate: swapPriceOneTokenWorth } = await getActualSwapRate(provider, {
      ...params,
      srcAmount: new BigNumber(10 ** assetUtils.getDecimals(chainId, srcToken)),
    })

    const swapPriceTradeAmount =
      params.swapPriceTradeAmount ||
      (
        await getActualSwapRate(provider, {
          ...params,
          srcAmount,
        })
      ).rate

    const slippage = swapPriceOneTokenWorth
      .minus(swapPriceTradeAmount)
      .abs()
      .div(swapPriceTradeAmount)
      .times(100)
    return slippage.dp(6)
  } catch (e) {
    return new BigNumber(0)
  }
}

export default {
  getSwapExpectedReturn,
  getActualSwapRate,
  getTradeSlippageRate,
}
